import React from 'react'
import { supabase } from '../lib/supabase'

/**
 * Top-level error boundary. A rendering crash anywhere in the tree shows a
 * recoverable fallback instead of a white screen, and fire-and-forgets the
 * error into activity_log so admins can see crashes agents never report.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Best-effort logging — never let the logger itself throw.
    try {
      supabase.auth.getSession().then(({ data }) => {
        const user = data?.session?.user
        if (!user) return
        supabase.from('activity_log').insert([{
          user_id: user.id,
          user_name: user.email || 'Unknown',
          action: 'error',
          entity_type: 'app_crash',
          entity_label: String(error?.message || error).slice(0, 200),
          details: { stack: String(info?.componentStack || '').slice(0, 1500) },
        }]).then(() => {})
      })
    } catch { /* swallow */ }
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 14,
          background: 'var(--bg, #0a0a0a)', color: 'var(--text, #eee)',
          fontFamily: 'system-ui, sans-serif', padding: 24, textAlign: 'center',
        }}>
          <div style={{ fontSize: 40 }}>⚠️</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Something went wrong</div>
          <div style={{ fontSize: 13, opacity: 0.7, maxWidth: 420 }}>
            The error has been logged. Reloading usually fixes it — if it keeps
            happening, tell Steve what you clicked.
          </div>
          <button
            onClick={() => { this.setState({ error: null }); window.location.reload() }}
            style={{
              marginTop: 8, padding: '10px 22px', fontSize: 14, fontWeight: 700,
              background: 'linear-gradient(135deg, #f97316, #ef4444)', color: '#fff',
              border: 'none', borderRadius: 10, cursor: 'pointer',
            }}
          >Reload app</button>
        </div>
      )
    }
    return this.props.children
  }
}
